import React from "react";
import { ExternalLink } from "lucide-react";
import { affiliateUrl, trackAffiliate } from "@/api";

const inline = (text, keyPrefix) => {
  // handle affiliate tokens {{affiliate:item|label}} and **bold**
  const nodes = [];
  const regex = /\{\{affiliate:([^|]+)\|([^}]+)\}\}/g;
  let last = 0;
  let m;
  let idx = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(...boldSplit(text.slice(last, m.index), `${keyPrefix}-t${idx}`));
    const item = m[1].trim();
    const label = m[2].trim();
    nodes.push(
      <button
        key={`${keyPrefix}-aff${idx}`}
        data-testid={`affiliate-link-${item}`}
        onClick={() => trackAffiliate(item, affiliateUrl(item))}
        className="inline-flex items-center gap-2 my-2 px-4 py-2 bg-[#E63946] hover:bg-[#D90429] text-white font-semibold rounded-md transition-colors"
      >
        {label}
        <ExternalLink size={16} />
      </button>
    );
    last = regex.lastIndex;
    idx++;
  }
  if (last < text.length) nodes.push(...boldSplit(text.slice(last), `${keyPrefix}-end`));
  return nodes;
};

const boldSplit = (text, keyPrefix) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={`${keyPrefix}-b${i}`}>{p.slice(2, -2)}</strong>;
    return <React.Fragment key={`${keyPrefix}-s${i}`}>{p}</React.Fragment>;
  });
};

export default function Markdown({ content }) {
  const blocks = content.split(/\n\n+/);
  return (
    <div className="article-body">
      {blocks.map((block, bi) => {
        const lines = block.split("\n");
        if (block.startsWith("## ")) return <h2 key={bi}>{block.slice(3)}</h2>;
        if (block.startsWith("### ")) return <h3 key={bi}>{block.slice(4)}</h3>;
        // image block ![alt](url)
        const img = block.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
        if (img)
          return (
            <img key={bi} src={img[2]} alt={img[1]} className="w-full rounded-lg my-6 border border-[#272b35]" />
          );
        if (block.startsWith("> ")) return <blockquote key={bi}>{inline(block.slice(2), `bq${bi}`)}</blockquote>;
        // table
        if (lines[0].includes("|") && lines.length > 1 && lines[1].includes("---")) {
          const header = lines[0].split("|").map((s) => s.trim()).filter(Boolean);
          const rows = lines.slice(2).map((r) => r.split("|").map((s) => s.trim()).filter(Boolean));
          return (
            <table key={bi}>
              <thead>
                <tr>{header.map((h, i) => <th key={i}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>{row.map((c, ci) => <td key={ci}>{c}</td>)}</tr>
                ))}
              </tbody>
            </table>
          );
        }
        // unordered list
        if (lines.every((l) => l.trim().startsWith("- "))) {
          return (
            <ul key={bi}>
              {lines.map((l, li) => <li key={li}>{inline(l.trim().slice(2), `ul${bi}-${li}`)}</li>)}
            </ul>
          );
        }
        // ordered list
        if (lines.every((l) => /^\d+\.\s/.test(l.trim()))) {
          return (
            <ol key={bi}>
              {lines.map((l, li) => <li key={li}>{inline(l.trim().replace(/^\d+\.\s/, ""), `ol${bi}-${li}`)}</li>)}
            </ol>
          );
        }
        return <p key={bi}>{inline(block, `p${bi}`)}</p>;
      })}
    </div>
  );
}
